/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { Actionable, Modal, Text, View } from '@az/starc-ui';
import styles from './GalleryStyles.module.scss';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useLabels } from '@/hooks/useLabels';
import dynamic from 'next/dynamic';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';
import { type ReviewType } from '../../../interface/types';
import { type ReviewPhotoMap } from '@/features/customReviews/hooks/useProductReviewImages';
import NextImage from '@/components/NextImage';

type GalleryViewProps = {
  isGalleryModalOpen: boolean;
  isReviewDetailsModalOpen: boolean;
  handleGalleryModalOpen: (isOpen: boolean) => void;
  images: string[] | undefined;
  photoCaptions: string[] | undefined | null;
  handleReviewDetailsModalOpen: (isOpen: boolean) => void;
  imageIndex: number;
  setImageIndex: (stateValue: number) => void;
  reviews: ReviewType[] | null | undefined | ReviewType;
  photoMap: ReviewPhotoMap | null | undefined;
};

const LazyReviewImageDetailsModal = dynamic(
  () =>
    import('../ReviewImageDetailsModal/ReviewImageDetailsModal').then(
      (mod) => mod.ReviewImageDetailsModal
    ),
  {
    loading: () => <ProgressModal noScroll />,
  }
);

const labelMap = {
  allImagesText: 'label_Reviews_AllImages',
};

export const GalleryView: React.FC<GalleryViewProps> = ({
  isGalleryModalOpen,
  isReviewDetailsModalOpen,
  handleGalleryModalOpen,
  images,
  photoCaptions,
  handleReviewDetailsModalOpen,
  imageIndex,
  setImageIndex,
  reviews,
  photoMap,
}) => {
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));

  const labels = useLabels(labelMap);

  return (
    <View>
      <Modal
        onClose={() => {
          handleGalleryModalOpen(false);
        }}
        size={{ s: 'full', m: 'full', l: 'large', xl: 'large' }}
        open={isGalleryModalOpen}
        hasCloseButton={true}
        headerTitle={!isDesktop ? labels.allImagesText : ' '}
        headerColor={isDesktop ? 'secondary' : 'primary'}
        className={styles.galleryViewModal}
      >
        <View.Item>
          <Text className={styles.allImagesText}>{labels.allImagesText}</Text>
        </View.Item>
        <View className={styles.modalImagesContainer} direction={'row'}>
          {images?.map((image, index) => {
            return (
              <Actionable
                key={`gallery-image-${image}-${index}`}
                onClick={() => {
                  setImageIndex(index);
                  handleReviewDetailsModalOpen(true);
                  handleGalleryModalOpen(false);
                }}
              >
                <View.Item className={styles.thumbnails}>
                  <NextImage
                    className={styles.images}
                    src={image}
                    width={130}
                    height={130}
                    alt={photoCaptions?.[index] || 'Review Image' + `${index + 1}`}
                  />
                </View.Item>
              </Actionable>
            );
          })}
        </View>
      </Modal>
      {isReviewDetailsModalOpen && (
        <LazyReviewImageDetailsModal
          isReviewDetailsModalOpen={isReviewDetailsModalOpen}
          handleReviewDetailsModalOpen={handleReviewDetailsModalOpen}
          images={images}
          photoCaptions={photoCaptions}
          imageIndex={imageIndex}
          setImageIndex={setImageIndex}
          reviews={reviews}
          photoMap={photoMap}
        />
      )}
    </View>
  );
};
